import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { analyticsOptions } from "@/features/analytics";
import type { AnalyticsParams } from "@/features/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { StatCard } from "@/components/ui/stat-card";
import { MousePointerClick, Users } from "lucide-react";

export function AnalyticsPage() {
  const search = useSearch({ strict: false }) as { shortCode?: string };
  const [shortCode, setShortCode] = useState(search?.shortCode ?? "");
  const [activeCode, setActiveCode] = useState(search?.shortCode ?? "");
  const [params, setParams] = useState<AnalyticsParams>({});

  const { data, isLoading, isError } = useQuery(analyticsOptions(activeCode, params));

  function handleSearch() {
    setActiveCode(shortCode);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">View detailed analytics for a link</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Input
              placeholder="Enter short code..."
              value={shortCode}
              onChange={(e) => setShortCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="max-w-xs font-mono"
            />
            <Button onClick={handleSearch} disabled={!shortCode}>
              Load Analytics
            </Button>
            <div className="ml-auto flex gap-2">
              <Input
                type="date"
                value={params.from ?? ""}
                onChange={(e) => setParams((p) => ({ ...p, from: e.target.value || undefined }))}
              />
              <Input
                type="date"
                value={params.to ?? ""}
                onChange={(e) => setParams((p) => ({ ...p, to: e.target.value || undefined }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {!activeCode && (
        <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          Enter a short code to view analytics
        </p>
      )}

      {isLoading && activeCode && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400" />
        </div>
      )}

      {isError && (
        <p className="py-12 text-center text-sm text-red-500 dark:text-red-400">
          Failed to load analytics. Check that the short code is valid.
        </p>
      )}

      {data && (
        <>
          {/* Stats */}
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard title="Total Clicks" value={data.totalClicks} icon={MousePointerClick} />
            <StatCard title="Unique Clicks" value={data.uniqueClicks} icon={Users} />
          </div>

          {/* Clicks Over Time */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Clicks Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              {data.clicksByDay.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No click data available</p>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={data.clicksByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="totalClicks" stroke="#2563eb" strokeWidth={2} name="Total" />
                    <Line type="monotone" dataKey="uniqueClicks" stroke="#16a34a" strokeWidth={2} name="Unique" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Breakdown Charts */}
          <div className="grid gap-6 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Countries</CardTitle>
              </CardHeader>
              <CardContent>
                {data.topCountries.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.topCountries} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Referrers</CardTitle>
              </CardHeader>
              <CardContent>
                {data.topReferrers.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.topReferrers} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Devices</CardTitle>
              </CardHeader>
              <CardContent>
                {data.topDevices.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.topDevices} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#059669" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
